import React, { useEffect, useMemo, useState } from "react"
import { Button, Empty, Modal, Skeleton, Tag, message } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { gen, getMeetDetailEditability, swimminglyApi } from "./utils"
import type { IClubSeasonInformation } from "./MeetActionsModal"
import { FaInfoCircle } from "react-icons/fa"
dayjs.extend(utc)

type TMeetActivityEvent = {
  meetActivityEventId: number
  eventType: string
  eventScope: string
  actorDisplayName: string
  actorNameSnapshot: string | null
  actorEmailSnapshot: string | null
  actorRoleSnapshot: number | null
  actorClubNameSnapshot: string | null
  actorType: string
  summary: string
  happenedAt: string
  isBackfilled: 0 | 1
  metadata?: {
    previousDisplayValue?: string | null
    nextDisplayValue?: string | null
  }
}

export default function MeetActivityLogTab({
  meetId,
  meetName,
  meetStatus,
  isVirtualMeet,
  meetClubSeasons,
  refreshKey,
}: {
  meetId: number
  meetName: string
  meetStatus: number | null
  isVirtualMeet: boolean
  meetClubSeasons: IClubSeasonInformation[]
  refreshKey: number
}): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<TMeetActivityEvent[]>([])
  const [infoVisible, setInfoVisible] = useState(false)

  useEffect(() => {
    let mounted = true

    async function loadActivity() {
      setLoading(true)
      const response: any = await swimminglyApi.get(gen(`/api/meetActivityLog/${meetId}`))

      if (!mounted) return

      if (response?.status === "success" && Array.isArray(response.events)) {
        setEvents(response.events)
      } else {
        setEvents([])
        message.error(response?.message || "Unable to load the meet activity log.")
      }

      setLoading(false)
    }

    loadActivity()

    return () => {
      mounted = false
    }
  }, [meetId, refreshKey])

  const nameEditability = useMemo(
    () =>
      getMeetDetailEditability({
        field: "name",
        meetStatus,
        meetClubSeasons,
      }),
    [meetClubSeasons, meetStatus],
  )

  const dateTimeEditability = useMemo(
    () =>
      getMeetDetailEditability({
        field: "dateTime",
        meetStatus,
        meetClubSeasons,
      }),
    [meetClubSeasons, meetStatus],
  )

  const participatingClubsEditability = useMemo(
    () =>
      getMeetDetailEditability({
        field: "participatingClubs",
        meetStatus,
        meetClubSeasons,
      }),
    [meetClubSeasons, meetStatus],
  )

  const entriesPhaseUnlocked = meetStatus === null || meetStatus === 0

  function getActorRoleTag(event: TMeetActivityEvent): { label: string; color: string } | null {
    if (event.actorType === "system") {
      return null
    }

    switch (Number(event.actorRoleSnapshot || 0)) {
      case 2:
        return { label: "Swimmingly Admin", color: "purple" }
      case 3:
        return { label: "League Admin", color: "cyan" }
      case 4:
        return { label: "Club Admin", color: "blue" }
      default:
        return null
    }
  }

  function renderActorLine(event: TMeetActivityEvent): JSX.Element | null {
    const actorName = event.actorType === "system" ? "System" : event.actorNameSnapshot?.trim() || ""
    const actorClub = event.actorClubNameSnapshot?.trim() || ""
    const actorEmail = event.actorEmailSnapshot?.trim() || ""
    const roleTag = getActorRoleTag(event)

    if (!actorName && !actorClub && !actorEmail) {
      return null
    }

    return (
      <div
        style={{
          fontSize: 11,
          color: "#475467",
          lineHeight: 1.3,
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        {actorName ? <span>{actorName}</span> : null}
        {roleTag ? (
          <Tag color={roleTag.color} style={{ marginInlineEnd: 0, lineHeight: "16px" }}>
            {roleTag.label}
          </Tag>
        ) : null}
        {actorClub ? <span>({actorClub})</span> : null}
        {actorEmail ? <span>{actorEmail}</span> : null}
      </div>
    )
  }

  function renderChangeDetailLine(event: TMeetActivityEvent): JSX.Element | null {
    const metadata = event.metadata || {}
    const hasPreviousValue = Object.prototype.hasOwnProperty.call(
      metadata,
      "previousDisplayValue",
    )
    const hasNextValue = Object.prototype.hasOwnProperty.call(metadata, "nextDisplayValue")

    if (!hasPreviousValue && !hasNextValue) {
      return null
    }

    const previousDisplayValue = metadata.previousDisplayValue?.trim() || "not set"
    const nextDisplayValue = metadata.nextDisplayValue?.trim() || "not set"

    return (
      <div style={{ fontSize: 12, color: "#475467", lineHeight: 1.4 }}>
        Changed from {previousDisplayValue} to {nextDisplayValue}.
      </div>
    )
  }

  return (
    <div>
      <div
        className="shadowed"
        style={{
          marginBottom: 12,
          padding: 12,
          background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#667085", marginBottom: 6 }}>
              {isVirtualMeet ? "Virtual meet activity" : "In-person meet activity"}
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              Activity Log for {meetName}
            </div>
            <div style={{ fontSize: 13, color: "#475467", maxWidth: 760 }}>
              Track a timeline of major meet setup changes, declarations milestones, heat sheet
              progress, and results publishing. Use the explainer below for what can still be edited
              after a meet has been set up.
            </div>
          </div>
          <Button icon={<FaInfoCircle />} type="link" onClick={() => setInfoVisible(true)}>
            Making changes to your swim meet
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="shadowed" style={{ padding: 12 }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : events.length === 0 ? (
        <div className="shadowed" style={{ padding: 18 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No activity has been recorded for this meet yet. New actions will appear here as they happen."
          />
        </div>
      ) : (
        <div className="shadowed" style={{ padding: 0, overflow: "hidden" }}>
          {events.map((event, index) => (
            <div
              key={event.meetActivityEventId}
              style={{
                padding: "8px 12px",
                borderBottom: index === events.length - 1 ? "none" : "1px solid rgba(0,0,0,0.06)",
                display: "grid",
                gap: 4,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 600, color: "#101828", fontSize: 13, lineHeight: 1.35 }}>
                  {event.summary}
                </span>
                {event.actorType === "system" ? <Tag color="gold">System</Tag> : null}
              </div>
              {renderChangeDetailLine(event)}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {renderActorLine(event) ? (
                  renderActorLine(event)
                ) : (
                  <div />
                )}
                <div style={{ fontSize: 11, color: "#667085", whiteSpace: "nowrap" }}>
                  {dayjs.utc(event.happenedAt).local().format("M/D/YYYY h:mm A")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        title="Making changes to your swim meet"
        open={infoVisible}
        onCancel={() => setInfoVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div style={{ fontSize: 13, color: "#475467", display: "grid", gap: 12 }}>
          <div>
            This is a {isVirtualMeet ? "virtual meet" : "traditional in-person meet"}. The
            Activity Log records major meet actions with what happened, when it happened, and who
            performed the action when that information is available.
          </div>
          <div>
            <strong>Changes that are allowed during the entries phase:</strong>
            <div>Meet name: {nameEditability.canEdit ? "currently editable" : "currently locked"}.</div>
            <div>
              Meet date and time: {dateTimeEditability.canEdit ? "currently editable" : "locked once results are published"}.
            </div>
            <div>
              Participating clubs: {participatingClubsEditability.canEdit ? "currently editable while the meet is still in entries status & the club has no entries" : "locked after the meet leaves entries status"}.
            </div>
            <div>
              Pool & course, host club, entry limits, and meet entries deadline: {entriesPhaseUnlocked ? "editable while the meet is still in entries status" : "locked because the meet is no longer in entries status"}.
            </div>
          </div>
          <div>
            <strong>Changes that are allowed during the heat sheet merged phase:</strong>
            <div>Meet name: {nameEditability.canEdit ? "currently editable" : "currently locked"}.</div>
            <div>
              Meet date and time: {dateTimeEditability.canEdit ? "currently editable" : "locked once results are published"}.
            </div>
          </div>
          <div>
            <strong>Changes that become restricted after meet creation:</strong>
            <div>
              Event Order: cannot be changed once the meet is set up.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}